import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { Box, Button, Text } from '@betty/moonlight';
import { QuitRegistrationBottomSheetProps } from './QuitRegistrationBottomSheet.types';
import { useDispatch, useSelector } from '@betty/toolkit/state';
import {
  selectRegistrationFormPromoId,
  selectRegistrationOffer,
  setPreventRemove,
} from '../../../state';
import { formatQuitRegistrationTitle } from '../../../utils/formatQuitRegistrationTitle';
import { formatReward } from '../../../utils/formatReward';
import { formatRewardDescription } from '../../../utils/formatQuitRegistrationDescription';
import RegistrationOfferImage from '../../../components/RegistrationOfferImage/RegistrationOfferImage';
import { useTranslation } from '@betty/i18n';
import { Link } from 'react-router';

function QuitRegistrationBottomSheet({
  close,
}: QuitRegistrationBottomSheetProps) {
  const { t } = useTranslation('registration');
  const dispatch = useDispatch();
  const offerId = useSelector(selectRegistrationFormPromoId) || -1;
  const offer = useSelector(selectRegistrationOffer(offerId, true));

  const rewards = useMemo(() => offer?.rewards || [], [offer?.rewards]);

  const title = useMemo(
    () => formatQuitRegistrationTitle(rewards.map(formatReward)),
    [rewards],
  );

  const rewardDescription = useMemo(
    () => rewards.map(formatRewardDescription).join(', '),
    [rewards],
  );

  const descriptionContent = useMemo(
    () => (
      <Text variant="body-m" color="darkSurfacePrimary">
        {t('completeRegistrationQuitPrefix')}
        <Text variant="body-m-semi" color="darkSurfacePrimary">
          {rewardDescription}
        </Text>
        {t('completeRegistrationQuitSuffix')}
      </Text>
    ),
    [t, rewardDescription],
  );

  const handleContinueRegistration = useCallback(() => {
    dispatch(setPreventRemove(true));
    close();
  }, [dispatch, close]);

  const handleCloseRegistration = useCallback(() => {
    close();
  }, [close]);

  useEffect(() => {
    dispatch(setPreventRemove(false));
  }, [dispatch]);

  if (!offer) {
    return null;
  }

  return (
    <Box>
      <Text variant="h5" color="darkSurfacePrimary">
        {title}
      </Text>
      <Box gap="space-400" marginTop="space-400">
        <Box
          flexDirection="row"
          justifyContent="flex-start"
          marginTop="space-400"
          marginBottom="space-300"
        >
          <Box flex={1} justifyContent="center" alignItems="center">
            <RegistrationOfferImage
              width={100}
              height={100}
              rewards={rewards}
              miniImageUrl={offer?.miniImageUrl || ''}
            />
          </Box>
          <Box flex={3} justifyContent="center" marginLeft="space-400">
            {descriptionContent}
          </Box>
        </Box>
        <Box flexDirection="row" justifyContent="space-between" gap="space-200">
          <Link to="/" style={{ flex: 1 }}>
            <Button
              variant="secondary"
              width="100%"
              locKey="quitRegistrationConfirm"
              locNS="registration"
              onPress={handleCloseRegistration}
            />
          </Link>
          <Button
            variant="primary"
            flex={1}
            locKey="quitRegistrationDecline"
            locNS="registration"
            onPress={handleContinueRegistration}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default memo(QuitRegistrationBottomSheet);
